import { useState } from "react";
import { FileText, Download, GitBranch } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { VideoTools } from "@/components/VideoTools";
import { toast } from "sonner";
import api from "@/lib/api";

export function AssetCard({ a, onLineage }) {
  const [open, setOpen] = useState(false);
  const [bgBusy, setBgBusy] = useState("");
  const [bgOut, setBgOut] = useState(null);
  const kind = a.kind || "image";
  const background = async (mode) => {
    setBgBusy(mode);
    try {
      const prompt = mode === "generate" ? window.prompt("Describe the new background scene") : null;
      if (mode === "generate" && !prompt) { setBgBusy(""); return; }
      const j = await api.post(`/media/${a.media_id}/background`, { mode, prompt, workspace_id: a.workspace_id || null });
      let job = j.data;
      while (job.status === "RUNNING") { await new Promise((r) => setTimeout(r, 3000)); job = (await api.get(`/creator/jobs/${job.job_id}`)).data; }
      if (job.status !== "COMPLETE") throw new Error(job.error || "Background edit failed");
      setBgOut(job.result); toast.success(`Background ${mode} ready`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBgBusy("");
  };
  return (
    <div data-testid="asset-card" className="rounded-xl border border-white/10 bg-black/30 overflow-hidden flex flex-col">
      <div className="relative bg-black/50">
        {kind === "image" && <img src={mediaUrl(a.url)} alt={a.title || a.prompt || ""} className="w-full aspect-square object-cover" />}
        {kind === "video" && <video src={mediaUrl(a.url)} controls playsInline className="w-full aspect-video bg-black" />}
        {kind === "audio" && <div className="p-4 aspect-video flex items-center"><audio src={mediaUrl(a.url)} controls className="w-full" /></div>}
        {kind === "text" && <div className="p-4 aspect-video flex items-center justify-center text-slate-500"><FileText size={32} /></div>}
        {kind === "web" && <iframe title={a.title || a.media_id} src={mediaUrl(a.url)} sandbox="" className="w-full aspect-video bg-white pointer-events-none" />}
        <span className="absolute top-2 left-2 text-[10px] font-mono px-2 py-0.5 rounded-md bg-black/60 border border-white/10 uppercase">{kind}</span>
        {a.score?.total != null && <span data-testid="asset-score" title={Object.values(a.score.reasons || {}).join(" · ")} className="absolute bottom-2 left-2 text-[10px] font-mono px-2 py-0.5 rounded-md bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-500/30">Vectra Score {a.score.total}/10</span>}
        {a.engine && <span className="absolute top-2 right-2 text-[10px] font-mono px-2 py-0.5 rounded-md bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">{a.engine}</span>}
      </div>
      <div className="p-3 space-y-1.5 flex-1">
        <div className="text-sm font-medium truncate" title={a.title || a.prompt}>{a.title || a.prompt || a.filename || a.media_id}</div>
        <div className="text-[11px] text-slate-500 font-mono flex flex-wrap gap-x-2">
          <span>{a.media_id}</span>
          {a.width && <span>{a.width}×{a.height}</span>}
          {a.duration && <span>{Number(a.duration).toFixed(1)}s</span>}
          {a.step && <span>· {a.step}</span>}
          {a.derived_from && <span className="text-fuchsia-300">↳ {a.derived_from}</span>}
        </div>
        <div className="flex gap-2 pt-1">
          <a data-testid="asset-download" href={mediaUrl(a.url)} download target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-white/10 hover:border-white/30"><Download size={11} /> Open</a>
          {onLineage && <button data-testid="asset-lineage-btn" onClick={() => onLineage(a)} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-white/10 hover:border-fuchsia-500/40"><GitBranch size={11} /> Lineage</button>}
          {kind === "video" && <button data-testid="asset-tools-btn" onClick={() => setOpen((v) => !v)} className="text-[11px] px-2 py-1 rounded-md border border-white/10 hover:border-cyan-500/40">{open ? "Hide tools" : "Edit tools"}</button>}
        </div>
        {open && kind === "video" && <VideoTools mediaId={a.media_id} />}
        {kind === "image" && (
          <div className="flex flex-wrap gap-1 pt-1" data-testid="asset-background-tools">
            {["remove", "blur", "brand", "generate"].map((m) => <button key={m} data-testid={`bg-${m}`} disabled={!!bgBusy} onClick={() => background(m)} className="text-[10px] px-2 py-0.5 rounded-md border border-white/10 hover:border-emerald-500/40 disabled:opacity-40">{bgBusy === m ? "…" : `bg: ${m}`}</button>)}
          </div>
        )}
        {bgOut && <a data-testid="bg-result" href={mediaUrl(bgOut.url)} target="_blank" rel="noreferrer" className="block mt-1"><img src={mediaUrl(bgOut.url)} alt={`background ${bgOut.mode}`} className="w-full rounded-md border border-emerald-500/30" /></a>}
      </div>
    </div>
  );
}

export function AssetGrid({ assets, onLineage }) {
  if (!assets.length) return null;
  return (
    <div data-testid="asset-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {assets.map((a) => <AssetCard key={a.media_id} a={a} onLineage={onLineage} />)}
    </div>
  );
}
